import React, { useMemo, useState } from 'react';
import { Stack, Tooltip, IconButton, Typography } from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TelegramIcon from '@mui/icons-material/Telegram';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Toast from '../Toast';

const normalizeAbsoluteUrl = (value) => {
  const fallback = typeof window !== 'undefined' ? window.location.href : 'https://r5id.com.br/news';
  try {
    return new URL(String(value || '').trim() || fallback, fallback).toString();
  } catch (error) {
    return fallback;
  }
};

const safeEncode = (value) => encodeURIComponent(String(value || '').trim());

const copyToClipboard = async (value) => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', 'readonly');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const XGlyph = ({ size = 16 }) => (
  <Typography
    component="span"
    aria-hidden="true"
    sx={{
      fontSize: `${size}px`,
      lineHeight: 1,
      fontWeight: 800,
      letterSpacing: '-0.02em',
      color: '#111'
    }}
  >
    X
  </Typography>
);

const ShareButtons = ({ title, description, url, iconButtonSx, spacing = 0.75 }) => {
  const [toast, setToast] = useState(null);

  const currentUrl = useMemo(() => normalizeAbsoluteUrl(url), [url]);
  const safeTitle = useMemo(
    () => String(title || 'Notícia').replace(/\s+/g, ' ').trim(),
    [title]
  );
  const safeDescription = useMemo(
    () => String(description || '').replace(/\s+/g, ' ').trim(),
    [description]
  );
  const shareText = `${safeTitle}${safeDescription ? ` — ${safeDescription}` : ''}`.trim();

  const channels = useMemo(() => ({
    // WhatsApp renders richer previews more consistently when the payload is only the URL.
    whatsapp: `https://wa.me/?text=${safeEncode(currentUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${safeEncode(currentUrl)}`,
    x: `https://twitter.com/intent/tweet?url=${safeEncode(currentUrl)}&text=${safeEncode(shareText)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${safeEncode(currentUrl)}`,
    telegram: `https://t.me/share/url?url=${safeEncode(currentUrl)}&text=${safeEncode(shareText)}`
  }), [currentUrl, shareText]);

  const setFeedback = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleNativeShare = async () => {
    if (!navigator?.share) {
      try {
        await copyToClipboard(currentUrl);
        setFeedback('Link copiado!');
      } catch (error) {
        setFeedback('Não foi possível copiar o link.', 'error');
      }
      return;
    }

    try {
      await navigator.share({
        title: safeTitle,
        text: shareText,
        url: currentUrl
      });
      setFeedback('Compartilhado com sucesso!');
    } catch (error) {
      if (error?.name !== 'AbortError') {
        setFeedback('Não foi possível compartilhar.', 'error');
      }
    }
  };

  const handleCopyLink = async () => {
    try {
      await copyToClipboard(currentUrl);
      setFeedback('Link copiado!');
    } catch (error) {
      setFeedback('Não foi possível copiar o link.', 'error');
    }
  };

  const defaultIconButtonSx = {
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: '1px solid rgba(0,0,0,0.10)',
    bgcolor: '#fff',
    color: '#3a3a3a',
    transition: 'all 0.18s ease',
    '&:hover': {
      bgcolor: '#f7f7f7',
      borderColor: 'rgba(0,0,0,0.16)'
    },
    '&:focus-visible': {
      outline: '3px solid #cc0000',
      outlineOffset: '2px'
    }
  };

  const buttonSx = iconButtonSx || defaultIconButtonSx;

  return (
    <>
      <Stack direction="row" spacing={spacing} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
        <Tooltip title="Compartilhar (nativo)">
          <IconButton onClick={handleNativeShare} sx={buttonSx} aria-label="Compartilhar notícia">
            <IosShareIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Compartilhar no WhatsApp">
          <IconButton
            component="a"
            href={channels.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            sx={buttonSx}
            aria-label="Compartilhar no WhatsApp"
          >
            <WhatsAppIcon sx={{ fontSize: 18, color: '#25D366' }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Compartilhar no Facebook">
          <IconButton
            component="a"
            href={channels.facebook}
            target="_blank"
            rel="noopener noreferrer"
            sx={buttonSx}
            aria-label="Compartilhar no Facebook"
          >
            <FacebookIcon sx={{ fontSize: 18, color: '#1877F2' }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Compartilhar no X">
          <IconButton
            component="a"
            href={channels.x}
            target="_blank"
            rel="noopener noreferrer"
            sx={buttonSx}
            aria-label="Compartilhar no X"
          >
            <XGlyph />
          </IconButton>
        </Tooltip>

        <Tooltip title="Compartilhar no LinkedIn">
          <IconButton
            component="a"
            href={channels.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            sx={buttonSx}
            aria-label="Compartilhar no LinkedIn"
          >
            <LinkedInIcon sx={{ fontSize: 18, color: '#0A66C2' }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Compartilhar no Telegram">
          <IconButton
            component="a"
            href={channels.telegram}
            target="_blank"
            rel="noopener noreferrer"
            sx={buttonSx}
            aria-label="Compartilhar no Telegram"
          >
            <TelegramIcon sx={{ fontSize: 18, color: '#229ED9' }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Copiar link">
          <IconButton onClick={handleCopyLink} sx={buttonSx} aria-label="Copiar link da notícia">
            <ContentCopyIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Toast
        message={toast?.message}
        type={toast?.type}
        onClose={() => setToast(null)}
      />
    </>
  );
};

export default ShareButtons;
